import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import {
  collection,
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  Button,
  TextField,
  Card,
  CardContent,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";

function Stores() {
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [sortOption, setSortOption] = useState("joinedOn");
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("manageStore");
  const [productSearch, setProductSearch] = useState("");
  const [productStatusFilter, setProductStatusFilter] = useState("All");
  const [productSortOption, setProductSortOption] = useState("name");

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "stores"), (snapshot) => {
      const fetchedStores = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        joinedOn: doc.data().joinedOn || { seconds: 0 },
      }));
      setStores(fetchedStores);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!selectedStore?.id) return;

    const unsubscribe = onSnapshot(
      collection(db, "stores", selectedStore.id, "products"),
      (snapshot) => {
        // Listener just in case selectedStore needs future features
      }
    );

    return () => unsubscribe();
  }, [selectedStore]);

  useEffect(() => {
    if (view !== "manageProducts") return;

    const unsubscribes = [];
    let temp = [];

    const promises = stores.map((store) =>
      new Promise((resolve) => {
        const unsubscribe = onSnapshot(
          collection(db, "stores", store.id, "products"),
          (snapshot) => {
            const storeProducts = snapshot.docs.map((doc) => ({
              storeId: store.id,
              storeName: store.storeName,
              id: doc.id,
              ...doc.data(),
            }));
            temp = [...temp, ...storeProducts];
            resolve();
          }
        );
        unsubscribes.push(unsubscribe);
      })
    );

    Promise.all(promises).then(() => {
      setAllProducts(temp);
    });

    return () => unsubscribes.forEach((unsub) => unsub());
  }, [view, stores]);

  const handleStoreClick = (storeId) => {
    const store = stores.find((s) => s.id === storeId);
    setSelectedStore(store);
  };

  const updateProductStatus = async (storeId, productId, status) => {
    try {
      await updateDoc(doc(db, "stores", storeId, "products", productId), {
        status,
      });
    } catch (err) {
      console.error("Failed to update product status", err);
    }
  };

  const filteredAndSortedStores = stores
    .filter((store) => {
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        store.storeName?.toLowerCase().includes(search) ||
        store.owner?.name?.toLowerCase().includes(search) ||
        store.owner?.phone?.includes(search);

      const matchesStatus =
        statusFilter === "All" || store.status === statusFilter;

      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      if (sortOption === "joinedOn") {
        return (b.joinedOn?.seconds || 0) - (a.joinedOn?.seconds || 0);
      } else if (sortOption === "totalOrders") {
        return (b.totalOrders || 0) - (a.totalOrders || 0);
      }
      return 0;
    });

  const getStatusColor = (status) => {
    switch (status) {
      case "Approved":
        return "success";
      case "Rejected":
        return "error";
      case "Pending":
        return "warning";
      default:
        return "default";
    }
  };

  const filteredProducts = allProducts
    .filter((product) => {
      const matchesSearch = product.name?.toLowerCase().includes(productSearch.toLowerCase());
      const matchesStatus = productStatusFilter === "All" || product.status === productStatusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      switch (productSortOption) {
        case "priceHigh":
          return (b.price || 0) - (a.price || 0);
        case "priceLow":
          return (a.price || 0) - (b.price || 0);
        case "stock":
          return (b.stock || 0) - (a.stock || 0);
        case "name":
        default:
          return a.name?.localeCompare(b.name);
      }
    });

  return (
    <div className="flex min-h-screen font-sans">
      <div className="w-1/4 p-4 bg-gray-100 border-r">
        <h2 className="text-lg font-semibold mb-4">Admin Panel</h2>
        <div className="space-y-3">
          <Button
            variant={view === "manageStore" ? "contained" : "outlined"}
            onClick={() => setView("manageStore")}
            fullWidth
          >
            🏪 Manage Stores
          </Button>
          <Button
            variant={view === "manageProducts" ? "contained" : "outlined"}
            onClick={() => setView("manageProducts")}
            fullWidth
          >
            👕 Manage Products
          </Button>
        </div>
      </div>

      <div className="flex-1 p-6 overflow-auto">
        {view === "manageStore" ? (
          <>
            <div className="flex flex-wrap gap-4 mb-6 items-center">
              <TextField
                label="Search by name, owner or phone..."
                variant="outlined"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-64"
              />
              <FormControl className="w-48">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="All">All Statuses</MenuItem>
                  <MenuItem value="Pending">Pending</MenuItem>
                  <MenuItem value="Approved">Approved</MenuItem>
                  <MenuItem value="Rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControl className="w-52">
                <InputLabel>Sort Option</InputLabel>
                <Select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  label="Sort Option"
                >
                  <MenuItem value="joinedOn">Sort by Join Date</MenuItem>
                  <MenuItem value="totalOrders">Sort by Orders</MenuItem>
                </Select>
              </FormControl>
            </div>

            {loading ? (
              <p>Loading stores...</p>
            ) : filteredAndSortedStores.length === 0 ? (
              <p>No stores found matching the criteria.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredAndSortedStores.map((store) => (
                  <Card
                    key={store.id}
                    onClick={() => handleStoreClick(store.id)}
                    className="cursor-pointer hover:bg-gray-50 transition"
                  >
                    <CardContent className="p-4 space-y-1">
                      <h3 className="font-semibold text-lg">{store.storeName}</h3>
                      <p><strong>Owner:</strong> {store.owner?.name || "N/A"}</p>
                      <p><strong>Phone:</strong> {store.owner?.phone || "N/A"}</p>
                      <p>
                        <strong>Status:</strong>{" "}
                        <Chip label={store.status} color={getStatusColor(store.status)} size="small" />
                      </p>
                      <p><strong>Total Orders:</strong> {store.totalOrders || 0}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="space-y-6">
            <h2 className="text-2xl font-semibold">Manage Products</h2>

            <div className="space-x-4 flex items-center">
              <TextField
                label="Search Products"
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                variant="outlined"
                size="small"
                className="w-64"
              />
              <FormControl className="w-40">
                <InputLabel>Status Filter</InputLabel>
                <Select
                  value={productStatusFilter}
                  onChange={(e) => setProductStatusFilter(e.target.value)}
                  label="Status Filter"
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value="In Stock">In Stock</MenuItem>
                  <MenuItem value="Out of Stock">Out of Stock</MenuItem>
                </Select>
              </FormControl>
              <FormControl className="w-40">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={productSortOption}
                  onChange={(e) => setProductSortOption(e.target.value)}
                  label="Sort By"
                >
                  <MenuItem value="name">Name</MenuItem>
                  <MenuItem value="priceHigh">Price High to Low</MenuItem>
                  <MenuItem value="priceLow">Price Low to High</MenuItem>
                  <MenuItem value="stock">Stock</MenuItem>
                </Select>
              </FormControl>
            </div>

            {filteredProducts.length === 0 ? (
              <p>No products found matching the criteria.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredProducts.map((product) => (
                  <Card key={product.id} className="cursor-pointer hover:bg-gray-50 transition">
                    <CardContent className="p-4 space-y-1">
                      <h3 className="font-semibold">{product.name}</h3>
                      <p><strong>Price:</strong> ₹{product.price}</p>
                      <p><strong>Stock:</strong> {product.stock}</p>
                      <p><strong>Status:</strong> {product.status}</p>

                      <Button
                        onClick={() =>
                          updateProductStatus(
                            product.storeId,
                            product.id,
                            product.status === "In Stock" ? "Out of Stock" : "In Stock"
                          )
                        }
                        variant="contained"
                        color={product.status === "In Stock" ? "error" : "success"}
                      >
                        Mark as {product.status === "In Stock" ? "Out of Stock" : "In Stock"}
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Stores;
